<script lang="ts" setup>
    import '../../../css/choice.css';
    import '../../../css/patientFlag.css';
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import _ from 'lodash';
    import * as Sound from '../../lib/Sound';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { usePatientFlagsStore } from '../../lib/Stores/PatientFlagsStore'
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { PrivilegeType, AppScreens, DatabaseIdTypes } from "../../Enums/ChoiceEnums";
    import { ButtonDisplaySize, IconTypes, ModalSize, DisplayStyle } from "../../Enums/UiEnums";
    import { IPatientFlagPatientAssignItem, IPatientFlagBasicSettingsBase, IPatientFlagDisplayOrder } from '../../lib/Models/Setup/PatientFlagSetupModel';
    import { IPatientServicePatientHeaderResponse } from '../../lib/Models/PatientModel';
    import { popAlert } from '../../lib/Notification';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import PatientTitle from "../../components/PatientTitle.vue"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import PatientFlagSetup from './PatientFlagSetup.vue';
    import ModalDialog from '../../components/ModalDialog.vue';
    import PatientFlagBadge from '../../components/Choice/Patient/PatientFlagBadge.vue';
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
    import Icon from '../../components/Icon.vue';
    import FlashMessage from '../../components/Messages/FlashMessage.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue';

    const windowHeight = ref<number>();
    const privilegesStore = usePrivilegesStore(initPinia());
    const patientFlagsStore = usePatientFlagsStore(initPinia());

    const props = defineProps<{
        patientFlagList?: IPatientFlagPatientAssignItem[],
        idType: DatabaseIdTypes,
        identifier: number,
        patientDetail?: IPatientServicePatientHeaderResponse
    }>();

    type IFlagTableItem = IPatientFlagPatientAssignItem & {displayOrder?: number};

    const formData = reactive<{
        tableData: IFlagTableItem[],
        patientDetail?: IPatientServicePatientHeaderResponse,
        showSettingsModal?: boolean,
        settingsModalKey?: number,       // Use to force reload of data when showing the modal
        showFlagOrder?: boolean,
        initialFlagDisplayOrder?: IPatientFlagDisplayOrder[],
        showSavedMessage?: boolean
    }>({
        tableData: []
    });

    onMounted(async () => {
        await privilegesStore.getScreens([AppScreens.PatientFlagAssign, AppScreens.PatientFlagSetup]);

        // Get the flag assignment values
        if (props.patientFlagList == undefined) {
            formData.tableData = await ChoiceApi.patientFlagSetup.getAssignList(props.idType, props.identifier) ?? [];
        }
        else {
            formData.tableData = props.patientFlagList ?? [];
        }

        // Apply user display order to the flags
        const userFlagDisplayOrderValues = await patientFlagsStore.getFlagDisplayOrder();
        if (userFlagDisplayOrderValues)
        {
            for(const userDisplayOrderItem of userFlagDisplayOrderValues)
            {
                const tableDataRow = formData.tableData.find(tableRow => tableRow.flagSetupId === userDisplayOrderItem.flagSetupId);
                if (tableDataRow)
                {
                    tableDataRow.displayOrder = userDisplayOrderItem.displayOrder;
                }
            }
        }

        // Add a value for display order to force a proper sort
        for(const row of formData.tableData)
            if (undefined === row.displayOrder)
                row.displayOrder = 1000 * 1000 * 1000;

        // Sort the flags: displayOrder (low to high), active (yes, then no), flag.longDescription (alphabetical)
        formData.tableData = _.orderBy(formData.tableData, 
            ["active", "displayOrder", "longDescription"],
            ["desc", "asc", "asc"]);

        // Assign a display order to each value based on the current display order
        formData.tableData = formData.tableData.map((flag, index) => {
            flag.displayOrder = 1+index;
            return flag;
        });

        // Remember the display order - we'll save the display order when we save if there has been a change
        formData.initialFlagDisplayOrder = currentFlagDisplayOrder.value;


        if (props.patientDetail == undefined) {
            formData.patientDetail = await ChoiceApi.patient.getSimpleHeader(props.idType, props.identifier);
        }
        else {
            formData.patientDetail = props.patientDetail;
        }
        windowResize();
    });

    const tableColumns = computed<ICol[]>(() => {
            const columns: ICol[] = [];
            columns.push(
                { Name: '', DataField:"patientAssigned", RenderMode: RenderModeType.Selection },
                { Name: 'Flag', DataField: 'empty' });

            if (formData.showFlagOrder)
            {
                columns.push({ Name: 'Display Order', DataField:"displayOrder", ExtraData:"DisplayOrder" });
            }

            columns.push(
                { Name: 'Description', DataField: 'longDescription', ExtraData: "LongDescription" },
                { Name: 'Displays in Portal(s)', ExtraData: "Portals" },
                { Name: 'Active', DataField: 'active', RenderMode: RenderModeType.BoolYesNo, ExtraData: "Active" });
            return columns;
        });

    const updatePrivilege = computed<boolean>(() => (privilegesStore.screens[AppScreens.PatientFlagAssign] >= PrivilegeType.ReadUpdateWrite) ? true : false);
    const allowAccessToPatientFlagSettings = computed<boolean>(() => privilegesStore.screens[AppScreens.PatientFlagSetup] === PrivilegeType.ReadUpdateWrite);

    const currentFlagDisplayOrder = computed(() =>
        formData.tableData.map((item) => {return {flagSetupId: item.flagSetupId, displayOrder: item.displayOrder}}));

    function windowResize() {
        windowHeight.value = window.innerHeight - 110;
    }

    async function SaveAssignment() {

        // Save flag assignments for the patient
        const flagAssignmentValues = {
            idType: props.idType,
            identifier: props.identifier,
            flagSetupList: formData.tableData.map((item) => {return {flagSetupId: item.flagSetupId, active: item.patientAssigned}})
        }

        const saveAssignPromise = ChoiceApi.patientFlagSetup.saveAssign(flagAssignmentValues).then(async result =>
        {
            if (!result?.successful) {
                await popAlert("Failed to assign patient flags.");
            }
            else
            {
                formData.showSavedMessage = true;
            }
        });

        // Save any flag order changes
        let saveDisplayOrderPromise;
        if (!_.isEqual(currentFlagDisplayOrder.value, formData.initialFlagDisplayOrder))
        {
            saveDisplayOrderPromise = patientFlagsStore.saveUserFlagDisplayOrder(currentFlagDisplayOrder.value);
        }
        formData.showFlagOrder = false;

        const promises = [saveAssignPromise];
        if (saveDisplayOrderPromise)
            promises.push(saveDisplayOrderPromise)

        await Promise.all(promises);
    }

    function showSettingsModal()
    {
        formData.settingsModalKey = 1+ (formData.settingsModalKey ?? 0);
        formData.showSettingsModal = true;
    }

    async function closedSettingsModal()
    {
        const flagSetupList = await ChoiceApi.patientFlagSetup.getSetupList();
        if (!flagSetupList)
            return;

        for(const flagSetup of flagSetupList)
        {
            const flag = formData.tableData.find(i => i.flagSetupId === flagSetup.flagSetupId);
            if (!flag)
                continue;
            flag.active = flagSetup.active;
            flag.colorId = flagSetup.colorId;
            flag.flagStyleIcon = flagSetup.flagStyleIcon;
            flag.iconId = flagSetup.iconId;
            flag.longDescription = flagSetup.longDescription;
            flag.shortDescription = flagSetup.shortDescription;
            flag.tooltip = flagSetup.tooltip;
        }
    }

    function activePortals(row: any): string[]
    {
        const portals = [];
        if (row.emrPortalActive)
            portals.push("EMR");
        if (row.billingPortalActive)
            portals.push("Billing");
        if (row.schedulerPortalActive)
            portals.push("Scheduler");
        if (row.documentPortalActive)
            portals.push("Document");
        return portals;
    }

    function moveFlagDisplayItemUp(flagSetupId: number)
    {
        const flagToMoveIndex = formData.tableData.findIndex(flag => flagSetupId === flag.flagSetupId);
        if (0 === flagToMoveIndex)
        {
            Sound.playSound(Sound.errorSoundBase64);
            return;
        }

        const upperFlag = formData.tableData[flagToMoveIndex-1];
        const lowerFlag = formData.tableData[flagToMoveIndex];

        swapDisplayOrder(upperFlag, lowerFlag);
    }

    function moveFlagDisplayItemDown(flagSetupId: number)
    {
        const flagToMoveIndex = formData.tableData.findIndex(flag => flagSetupId === flag.flagSetupId);
        if (formData.tableData.length === (1+flagToMoveIndex))
        {
            Sound.playSound(Sound.plinkSoundBase64);
            return;
        }

        const upperFlag = formData.tableData[flagToMoveIndex];
        const lowerFlag = formData.tableData[flagToMoveIndex+1];

        swapDisplayOrder(upperFlag, lowerFlag);
    }

    function swapDisplayOrder(upperFlag: IFlagTableItem, lowerFlag: IFlagTableItem)
    {
        const nextLowerFlagDisplayOrder = upperFlag.displayOrder;
        upperFlag.displayOrder = lowerFlag.displayOrder;
        lowerFlag.displayOrder = nextLowerFlagDisplayOrder;

        formData.tableData = _.orderBy(formData.tableData, o => o.displayOrder);
    }

    const visibleTableRows = computed<IFlagTableItem[]|undefined>(() =>
        formData.tableData?.filter(row => 
            row.active || (!row.active || row.patientAssigned)));
</script>

<style scoped>
    .fixedTableWidth {
        width: 400px;
    }
    .fixedHeaderWidth {
        width: 300px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <FlashMessage v-model:showMessage="formData.showSavedMessage" :displayStyle="DisplayStyle.Success">
        <Icon :icon="IconTypes.CheckMark" :displayStyle="DisplayStyle.Success"/> Saved
    </FlashMessage>


    <div class="row">
        <div class="col isalus-patient-header text-truncate">
            <PatientTitle :patientDetail="formData.patientDetail" />
        </div>
    </div>
    <div class="row mb-0 mt-2">
        <div class="col">
            <span class="isalus-screen-header">
                Patient Flag Assign
            </span>
        </div>
        <div class="col text-end">
            <ChoiceButton 
                    :action="ActionButtonStyle.NoIcon" 
                    :size="ButtonDisplaySize.Small"  
                    tooltip="Change display order"
                    :displayStyle="formData.showFlagOrder ? DisplayStyle.Success : DisplayStyle.Primary"
                    @click="formData.showFlagOrder = !formData.showFlagOrder">
                <Icon :icon="IconTypes.Edit" :displayStyle="DisplayStyle.Light" />
                <Icon :icon="IconTypes.SortNumeric" :displayStyle="DisplayStyle.Light"/>
                Order
            </ChoiceButton>

            <ActionButtonV2 :action="ActionButtonStyle.Setting"
                    v-if="allowAccessToPatientFlagSettings"
                    :size="ButtonDisplaySize.Small"  
                    tooltip="Edit Flag Settings"
                    :performAction="async() => showSettingsModal()" />

            <span class="ms-2">
                <ActionButtonV2 
                        :action="ActionButtonStyle.Save" 
                        text="Save"
                        :size="ButtonDisplaySize.Small"  
                        :disabled="!updatePrivilege" 
                        :performAction="SaveAssignment" />
            </span>

        </div>
    </div>
    <div class="row mt-2">
        <div>
            <p style="font-size: 0.75rem;">                
                <ChoiceTable 
                    ref="SetupTable"
                    :columns="tableColumns" 
                    :data="visibleTableRows" 
                    :show-filters="false" 
                    :allowSorting="false"
                    :height="windowHeight">
                    <template #cell="{col, row, value}">
                        <template v-if="'Flag' === col.Name">
                            <PatientFlagBadge :flag="<IPatientFlagBasicSettingsBase>row" />
                        </template>
                        <template v-if="'DisplayOrder' === col.ExtraData">
                            <small>
                                <ChoiceButton :action="ActionButtonStyle.NoIcon"
                                        :icon="IconTypes.ChveronUp" 
                                        :size="ButtonDisplaySize.XS" 
                                        @click="() => moveFlagDisplayItemUp(row.flagSetupId)" />
                                <ChoiceButton :action="ActionButtonStyle.NoIcon"
                                        :icon="IconTypes.ChveronDown" 
                                        :size="ButtonDisplaySize.XS" 
                                        @click="() => moveFlagDisplayItemDown(row.flagSetupId)"  />
                            </small>
                        </template>
                        <template v-if="'LongDescription' === col.ExtraData">
                            {{ value }}
                            <MessageBadge v-if="!row['active']">
                                Inactive
                            </MessageBadge>
                        </template>
                        <template v-if="'Portals' === col.ExtraData">
                            {{ activePortals(row).join(", ") }}
                        </template>                        
                    </template>
                </ChoiceTable>
            </p>
        </div>
    </div>

    <ModalDialog v-model:show="formData.showSettingsModal" @hidden="closedSettingsModal" :size="ModalSize.FullScreen" v-if="allowAccessToPatientFlagSettings">
        <template #title>
            Patient Flag Setup
        </template>
        <PatientFlagSetup :key="formData.settingsModalKey" />
    </ModalDialog>
</template>

